import { gql, useLazyQuery } from "@apollo/client";
import React, { useEffect, useState, useContext } from "react";
import { convertToCSV } from "@solsys/solsys-common";
import {
  OptionsMenu,
  Button,
  NotificationContext,
  ResponsiveTableView,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { FaPlus, FaCog, FaFileCsv } from "react-icons/fa";
import { withTimestampSortOptions, resetListSort } from "../../listSort";
import useApolloListData from "../../useApolloListData";

const gqlQuery = gql`
  query regionList($region_list_request: region_list_request) {
    region(region_list_request: $region_list_request) {
      edges {
        id
        name
        sequence
        status
      }
      totalCount
    }
  }
`;

export const ListOptions = {
  sortBy: "sortBy",
  sortDirection: "sortDirection",
  name: "name",
};

export default function RegionList({ id: entityId, action, ...props }) {
  const { addNotification } = useContext(NotificationContext);
  const isAdd = action === "add";

  const [requestState, setRequestState] = props.state;
  let { sortBy, sortDirection, name } = requestState;
  const setListOptions = (state) =>
    setRequestState({ ...requestState, ...state });
  const [optionSortBy, setOptionSortBy] = useState(sortBy);
  const [optionSortDirection, setOptionSortDirection] = useState(sortDirection);
  const [optionName, setOptionName] = useState(name);
  const applyOptions = () => {
    setListOptions({
      sortBy: optionSortBy,
      sortDirection: optionSortDirection,
      name: optionName,
    });
  };
  const resetOptions = () => {
    const defaults = resetListSort();
    setOptionSortDirection(defaults.sortDirection);
    setOptionSortBy(defaults.sortBy);
    setOptionName("");
  };

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [limit, setLimit] = useState(100);

  const sortByOptions = withTimestampSortOptions([
    { text: "ID", sortBy: "id" },
    { text: "Name", sortBy: "name" },
    { text: "Order", sortBy: "sequence" },
  ]);
  const sortDirectionOptions = [
    { text: "A to Z", sortDirection: "ASC" },
    { text: "Z to A", sortDirection: "DESC" },
  ];

  let variables = {
    region_list_request: {
      filters: [],
      pagination: { limit },
      sorting: [],
    },
  };
  if (sortBy && sortDirection) {
    let sortOption = sortByOptions.find((v) => v.sortBy === sortBy);
    variables.region_list_request.sorting.push({
      field: sortBy,
      direction: sortDirection,
      entity: sortOption?.sortEntity,
    });
  }
  if (name) {
    variables.region_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${name}%`,
    });
  }

  const [csvQuery, { loading: csvLoading }] = useLazyQuery(gqlQuery);
  const [refetch, { loading, data }] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 5000,
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });
  const list = useApolloListData(data, (localData) => localData?.region?.edges);
  useEffect(() => {
    if (props.onStateChange) props.onStateChange(requestState);
    refetch({ variables });
  }, [requestState, limit]);
  const count = data?.region?.totalCount ?? 0;
  if (props.isMaster && props.autoSelect && !loading && !isAdd && !entityId) {
    props.onSelect(list.length ? list[0].id : 0);
  }

  return (
    <OptionsMenu isOpen={isFilterMenuOpen}>
      <OptionsMenu.Menu>
        <div className="options-tools">
          <Button
            onClick={() => {
              applyOptions();
              setIsFilterMenuOpen(false);
            }}
            loading={loading}
          >
            Apply
          </Button>
          <button onClick={resetOptions}>Reset</button>
          <button onClick={() => setIsFilterMenuOpen(false)}>Close</button>
        </div>

        <div className="options-section">
          <h3>Filters</h3>
          <div className="option">
            <label>Region Name:</label>
            <input
              value={optionName}
              onChange={(e) => setOptionName(e.target.value)}
            />
          </div>
        </div>

        <div className="options-section">
          <h3>Sorting</h3>
          <div className="option">
            <label>Sort By:</label>
            <div>
              <select
                value={optionSortBy}
                onChange={(event) => setOptionSortBy(event.target.value)}
              >
                {sortByOptions.map((option) => (
                  <option key={option.text} value={option.sortBy}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="option">
            <label>Sort Order:</label>
            <div>
              <select
                value={optionSortDirection}
                onChange={(event) => setOptionSortDirection(event.target.value)}
              >
                {sortDirectionOptions.map((option) => (
                  <option key={option.text} value={option.sortDirection}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="options-tools">
          <Button
            onClick={() => {
              applyOptions();
              setIsFilterMenuOpen(false);
            }}
            loading={loading}
          >
            Apply
          </Button>
          <button onClick={resetOptions}>Reset</button>
          <button onClick={() => setIsFilterMenuOpen(false)}>Close</button>
        </div>
      </OptionsMenu.Menu>
      <OptionsMenu.Layout>
        <div className="page-header">
          <span className="page-header-title">Regions</span>
          <div className="page-header-options">
            <Button
              onClick={() => {
                variables.region_list_request.pagination = { limit: 10000 };
                csvQuery({ variables }).then((response) => {
                  const csvData = convertToCSV(
                    response?.data?.region?.edges.map((entity) => ({
                      ID: entity.id,
                      Name: entity.name,
                      Order: entity.sequence,
                      Status: entity.status ? "Active" : "Inactive",
                    }))
                  );
                  const blob = new Blob([csvData], {
                    type: "text/csv;charset=utf-8;",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute(
                    "download",
                    `regions_${DateTime.now()
                      .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
                      .replace(/:/g, "-")
                      .replace(/, /g, "_")}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                });
              }}
              loading={csvLoading}
            >
              <FaFileCsv />
            </Button>
            {props.isMaster && (
              <button onClick={() => props.onSelect(0)}>
                <FaPlus />
              </button>
            )}
            <button onClick={() => setIsFilterMenuOpen(true)}>
              <FaCog />
            </button>
          </div>
        </div>

        <ResponsiveTableView
          style={{ height: "100%" }}
          loading={loading}
          headers={[
            { label: "ID" },
            { label: "Name" },
            { label: "Order" },
            { label: "Status" },
          ]}
          data={list.map((entity) => ({
            id: entity.id,
            ID: entity.id,
            Name: entity.name,
            Order: entity.sequence ?? "—",
            Status: entity.status ? "Active" : "Inactive",
            selected: String(entity.id) === String(entityId) && !isAdd,
          }))}
          identityColumnKey="ID"
          onRowClick={(row) => {
            props.onSelect(row.id, true);
          }}
          totalCount={count}
          addMoreResults={() => {
            setLimit(limit + 100);
          }}
        />
      </OptionsMenu.Layout>
    </OptionsMenu>
  );
}
